import type { ReactElement } from 'react';

import type { BaselineBand, SeriesPoint } from '../api/anomaly';

/* The "observed series against baseline" chart on the finding detail screen.
 *
 * **Feature-local, not `Sparkline`.** `Sparkline` draws a line and nothing else, and its own comment
 * says a band is out of scope. This one restates three things the card already states in words — the
 * observed line, the expected band, and which point is the subject under test — so a reader who
 * cannot see the shape has lost nothing.
 *
 * Follows `Sparkline`'s conventions: a fixed user-unit viewBox, `preserveAspectRatio="none"` so the
 * line fills the card's width, and a half-stroke inset so the extreme points are not clipped. Draws
 * nothing below two points — one reading is not a trend, and a flat line through it would assert a
 * stability nobody measured.
 */

const VIEW_W = 100;
const VIEW_H = 40;
const INSET = 2;

export interface SeriesChartProps {
  readonly points: readonly SeriesPoint[];
  readonly band: BaselineBand | null;
  /** Accessible name — the caption states the movement as a single delta; this carries the shape. */
  readonly label: string;
}

export function SeriesChart({
  points,
  band,
  label,
}: SeriesChartProps): ReactElement | null {
  if (points.length < 2) return null;

  const values = points.map((point) => point.value);
  const lows = band === null ? [] : [band.low];
  const highs = band === null ? [] : [band.high];
  const low = Math.min(...values, ...lows);
  const high = Math.max(...values, ...highs);
  const span = high - low || 1;

  const y = (value: number): number =>
    VIEW_H - INSET - ((value - low) / span) * (VIEW_H - INSET * 2);
  const x = (index: number): number => (index / (values.length - 1)) * VIEW_W;

  const line = values
    .map(
      (value, index) =>
        `${index === 0 ? 'M' : 'L'}${x(index).toFixed(2)} ${y(value).toFixed(2)}`,
    )
    .join(' ');

  const subjectX = x(values.length - 1);
  const subjectY = y(values[values.length - 1] ?? low);

  return (
    <svg
      className="lq-serieschart"
      viewBox={`0 0 ${String(VIEW_W)} ${String(VIEW_H)}`}
      preserveAspectRatio="none"
      role="img"
      aria-label={label}
    >
      {band !== null && (
        <rect
          className="lq-serieschart__band"
          x={0}
          width={VIEW_W}
          y={Math.min(y(band.high), y(band.low))}
          height={Math.abs(y(band.low) - y(band.high))}
        />
      )}
      <path className="lq-serieschart__line" d={line} />
      {/* Non-scaling so the marker stays a circle however wide the card is. */}
      <circle
        className="lq-serieschart__subject"
        cx={subjectX}
        cy={subjectY}
        r={2}
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}
